import {useMemo} from 'react';
import {SensorAvailabilityStatus} from '@lib/infrastructure';
import {WorkspaceOccupancyStateForBuilding, WorkspaceReservation} from '@lib/store';

type Args = {
  floorId: string;
  sensorData: WorkspaceOccupancyStateForBuilding | null;
  connectionReservations: WorkspaceReservation[];
  doNotShowSensorData: boolean;
};

export const useDesksGroupedByStatus = ({floorId, sensorData, connectionReservations, doNotShowSensorData}: Args) => {
  const groupedDesks = useMemo(() => {
    if (doNotShowSensorData || !sensorData) {
      return {available: [], occupied: [], unknown: []};
    }

    const allDesks = sensorData.states.flatMap((state) => state.desks);

    const bookedDeskIds = new Set(
      connectionReservations.filter((res) => res.floorId === floorId && res.deskId).map((res) => res.deskId),
    );

    const grouped = {
      available: [] as typeof allDesks,
      occupied: [] as typeof allDesks,
      unknown: [] as typeof allDesks,
    };

    for (const desk of allDesks) {
      const {state, id} = desk;

      // Sensor states
      const isAvailable = state === SensorAvailabilityStatus.Available;
      const isOccupied = state === SensorAvailabilityStatus.Occupied;

      // Booking state
      const isBooked = bookedDeskIds.has(id);

      if (isAvailable && isBooked) {
        // If the desk is available according to the sensor but it has a reservation, we consider the state unknown
        grouped.unknown.push(desk);
      } else if (isAvailable) {
        grouped.available.push(desk);
      } else if (isOccupied) {
        grouped.occupied.push(desk);
      } else {
        grouped.unknown.push(desk);
      }
    }

    return grouped;
  }, [doNotShowSensorData, sensorData, connectionReservations, floorId]);

  return groupedDesks;
};
